// Voter Awareness Blog Articles - Static Data
export const voterAwarenessBlogs = [
  {
    id: 1,
    slug: "why-your-vote-matters",
    title: "Why Your Vote Matters: The Power of Democracy",
    subtitle: "Every vote is a voice in shaping Bihar's future",
    excerpt: "Discover why casting your vote is not just a right, but a responsibility that shapes the future of our democracy.",
    category: "Voter Awareness",
    author: "Bihar Election Commission",
    readTime: "5 min read",
    date: "October 20, 2025",
    featured: true,
    image: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=800",
    content: `
      <h2>The Foundation of Democracy</h2>
      <p>Voting is the cornerstone of democracy. In Bihar, with over 7 crore eligible voters, each vote carries immense power to bring change and progress. Your single vote can decide the future of your constituency and the entire state.</p>
      
      <h2>Why Every Vote Counts</h2>
      <ul>
        <li><strong>Representation:</strong> Your vote ensures your voice is heard in the government</li>
        <li><strong>Accountability:</strong> Elected representatives are answerable to you</li>
        <li><strong>Change:</strong> Vote for the development and policies you believe in</li>
        <li><strong>Rights:</strong> Exercise the fundamental right given by our Constitution</li>
      </ul>
      
      <h2>Historical Impact</h2>
      <p>In Bihar's 2020 elections, several constituencies were decided by margins of less than 1,000 votes. In 2015, the Gaya Town constituency was won by just 266 votes. This proves that every single vote matters!</p>
      
      <h2>Your Vote = Your Future</h2>
      <p>When you vote, you're not just choosing a candidate—you're deciding on education policies, healthcare facilities, infrastructure development, and employment opportunities in Bihar. Your vote impacts:</p>
      <ul>
        <li>Quality of schools and colleges</li>
        <li>Healthcare and medical facilities</li>
        <li>Road and transport infrastructure</li>
        <li>Employment and business opportunities</li>
        <li>Law and order in your area</li>
      </ul>
      
      <blockquote>
        "Democracy is not just a spectator sport. It requires active participation from every citizen." - Dr. B.R. Ambedkar
      </blockquote>
      
      <h2>Make Your Voice Count</h2>
      <p>On election day, exercise your right. Visit your polling booth, cast your vote, and be proud to be a responsible citizen of Bihar and India!</p>
    `,
    tags: ["Democracy", "Voting Rights", "Bihar Elections", "Citizen Duty"]
  },
  {
    id: 2,
    slug: "how-to-vote-step-by-step",
    title: "How to Vote: Complete Step-by-Step Guide",
    subtitle: "A simple guide to the voting process in Bihar",
    excerpt: "Learn the complete voting process from finding your polling station to casting your vote successfully.",
    category: "Election Process",
    author: "Election Support Team",
    readTime: "7 min read",
    date: "October 18, 2025",
    featured: true,
    image: "https://images.unsplash.com/photo-1540910419892-4a36d2c3266c?w=800",
    content: `
      <h2>Before Election Day</h2>
      
      <h3>1. Check Your Registration</h3>
      <p>Verify that your name is on the electoral roll:</p>
      <ul>
        <li>Visit the National Voter Service Portal (nvsp.in)</li>
        <li>Enter your details to search</li>
        <li>Note down your polling station location</li>
      </ul>
      
      <h3>2. Get Your Voter ID</h3>
      <p>Acceptable documents for voting:</p>
      <ul>
        <li>Voter ID Card (EPIC)</li>
        <li>Aadhaar Card</li>
        <li>Passport</li>
        <li>Driving License</li>
        <li>PAN Card</li>
        <li>Service Identity Card with photograph</li>
      </ul>
      
      <h2>On Election Day</h2>
      
      <h3>Step 1: Reach Your Polling Station</h3>
      <p>Arrive early (polls open at 7 AM) to avoid crowds. Bring your voter ID or acceptable document.</p>
      
      <h3>Step 2: Queue at Entry</h3>
      <p>Separate queues for men and women. Maintain social distancing and follow COVID protocols if applicable.</p>
      
      <h3>Step 3: Document Verification</h3>
      <p>Show your ID to the polling officer. Your name will be verified in the electoral roll.</p>
      
      <h3>Step 4: Get Ink Mark</h3>
      <p>Indelible ink will be applied on your left index finger to prevent duplicate voting.</p>
      
      <h3>Step 5: Enter the Voting Booth</h3>
      <p>You'll be directed to the Electronic Voting Machine (EVM).</p>
      
      <h3>Step 6: Cast Your Vote</h3>
      <ol>
        <li>Look at the list of candidates on the Ballot Unit</li>
        <li>Press the blue button next to your chosen candidate</li>
        <li>The red lamp will glow and a beep sound confirms your vote</li>
        <li>You can press ONLY ONCE</li>
      </ol>
      
      <h3>Step 7: Exit and Collect Voter Slip</h3>
      <p>Collect your "I Voted" slip at the exit as proof of participation.</p>
      
      <div class="info-box">
        <h4>⏰ Voting Hours</h4>
        <p>7:00 AM to 6:00 PM</p>
        <p>If you're in queue at 6 PM, you can still vote!</p>
      </div>
      
      <h2>Important Dos and Don'ts</h2>
      
      <h3>✅ DO:</h3>
      <ul>
        <li>Carry a valid ID proof</li>
        <li>Go alone to the voting booth</li>
        <li>Keep your phone on silent</li>
        <li>Be patient and respectful</li>
      </ul>
      
      <h3>❌ DON'T:</h3>
      <ul>
        <li>Carry mobile phones inside voting booth</li>
        <li>Take photographs of your vote</li>
        <li>Canvass or campaign near polling station (100m radius ban)</li>
        <li>Argue with polling officials</li>
      </ul>
      
      <blockquote>
        "Your vote is your voice. Make it count!"
      </blockquote>
    `,
    tags: ["Voting Guide", "EVM", "Election Process", "First Time Voter"]
  },
  {
    id: 3,
    slug: "first-time-voter-guide",
    title: "First Time Voter? Here's Everything You Need to Know",
    subtitle: "A comprehensive guide for youth voters in Bihar",
    excerpt: "Excited to vote for the first time? Learn everything about registration, documents, and what to expect on election day.",
    category: "Youth Awareness",
    author: "Youth Outreach Team",
    readTime: "6 min read",
    date: "October 15, 2025",
    featured: true,
    image: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800",
    content: `
      <h2>Welcome to Democracy! 🎉</h2>
      <p>Congratulations on becoming eligible to vote! As a first-time voter in Bihar, you're about to exercise one of the most important rights in a democracy. This guide will help you navigate the entire process with confidence.</p>
      
      <h2>Am I Eligible to Vote?</h2>
      <p>You can vote if you:</p>
      <ul>
        <li>Are 18 years or older on the qualifying date</li>
        <li>Are an Indian citizen</li>
        <li>Are a resident of Bihar (or your constituency)</li>
        <li>Are of sound mind and not disqualified</li>
      </ul>
      
      <h2>How to Register as a Voter</h2>
      
      <h3>Online Registration</h3>
      <ol>
        <li>Visit <strong>nvsp.in</strong> or <strong>voters.eci.gov.in</strong></li>
        <li>Click on "New Voter Registration"</li>
        <li>Fill Form 6 online</li>
        <li>Upload required documents:
          <ul>
            <li>Age proof (Birth certificate/10th certificate)</li>
            <li>Address proof (Aadhaar/Ration card)</li>
            <li>Recent photograph</li>
          </ul>
        </li>
        <li>Submit the form</li>
        <li>Note the reference number for tracking</li>
      </ol>
      
      <h3>Offline Registration</h3>
      <p>Visit your nearest Electoral Registration Office or BLO (Booth Level Officer) with filled Form 6 and documents.</p>
      
      <h2>Understanding Your Voter ID</h2>
      <p>Your Voter ID (EPIC - Electors Photo Identity Card) contains:</p>
      <ul>
        <li>Your name and photo</li>
        <li>Father's/Husband's name</li>
        <li>Address</li>
        <li>Unique Voter ID number</li>
        <li>Polling station details</li>
      </ul>
      
      <h2>Why Should I Vote?</h2>
      
      <h3>1. Shape Your Future</h3>
      <p>The government makes decisions on education, jobs, infrastructure—all affecting your life directly.</p>
      
      <h3>2. Honor Democracy</h3>
      <p>Millions fought for this right. Don't let it go to waste.</p>
      
      <h3>3. Hold Leaders Accountable</h3>
      <p>Your vote is the strongest tool to demand better governance.</p>
      
      <h3>4. Represent Your Generation</h3>
      <p>Youth voter turnout matters. Make your generation's voice heard!</p>
      
      <h2>Election Day Checklist 📋</h2>
      
      <div class="checklist">
        <h4>Before You Leave Home:</h4>
        <ul class="task-list">
          <li>✅ Voter ID or alternative ID</li>
          <li>✅ Check polling station location</li>
          <li>✅ Wear comfortable clothes</li>
          <li>✅ Carry water bottle</li>
          <li>✅ Leave mobile in car/locker (not allowed inside booth)</li>
        </ul>
      </div>
      
      <h2>Common First-Timer Questions</h2>
      
      <h3>Q: What if I don't have a Voter ID yet?</h3>
      <p>You can vote with alternative IDs like Aadhaar, Passport, Driving License, or PAN card.</p>
      
      <h3>Q: Can I see who others are voting for?</h3>
      <p>No! Voting is secret. No one knows who you voted for.</p>
      
      <h3>Q: What if I make a mistake while voting?</h3>
      <p>Once you press the button, the vote is cast. Think carefully before pressing!</p>
      
      <h3>Q: Can I take a selfie with my ballot?</h3>
      <p>No! Phones are not allowed in the voting booth. Taking photos inside is illegal.</p>
      
      <h2>Make a Difference!</h2>
      <p>As a first-time voter, you're joining millions of Indians in the world's largest democracy. Your vote matters, your voice counts. Don't just vote—encourage your friends and family to vote too!</p>
      
      <blockquote>
        "You're not just casting a vote, you're casting your future." - Young Voters Initiative
      </blockquote>
      
      <div class="cta-box">
        <h4>Ready to Make History?</h4>
        <p>Register Now | Check Polling Station | Download Voter Helpline App</p>
      </div>
    `,
    tags: ["First Time Voter", "Youth", "Voter Registration", "Election Guide"]
  },
  {
    id: 4,
    slug: "understanding-democracy",
    title: "Understanding Democracy: Power to the People",
    subtitle: "What democracy means and why it matters to Bihar",
    excerpt: "Learn about democratic principles, how they work in Bihar, and why citizen participation is crucial for progress.",
    category: "Democracy Education",
    author: "Civic Education Team",
    readTime: "8 min read",
    date: "October 12, 2025",
    featured: false,
    image: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800",
    content: `
      <h2>What is Democracy?</h2>
      <p>Democracy comes from Greek words "demos" (people) and "kratos" (power), meaning "power to the people." India is the world's largest democracy with over 90 crore voters.</p>
      
      <h2>Democratic Principles in India</h2>
      
      <h3>1. Universal Adult Franchise</h3>
      <p>Every citizen aged 18+ can vote regardless of caste, religion, gender, or economic status.</p>
      
      <h3>2. Free and Fair Elections</h3>
      <p>The Election Commission ensures unbiased elections through EVMs, model code of conduct, and strict monitoring.</p>
      
      <h3>3. Representative Government</h3>
      <p>You elect MLAs who represent your interests in the Bihar Legislative Assembly.</p>
      
      <h3>4. Accountability</h3>
      <p>Elected representatives must answer to citizens. Poor performance leads to electoral defeat.</p>
      
      <h2>Bihar's Democratic Journey</h2>
      <p>Since 1952, Bihar has seen 16 assembly elections. Voter turnout has increased from 49% (1952) to 57% (2020), showing growing democratic consciousness.</p>
      
      <h2>How Your Vote Shapes Bihar</h2>
      <ul>
        <li><strong>Development:</strong> Elect leaders committed to roads, electricity, water</li>
        <li><strong>Education:</strong> Better schools, colleges, and skill development</li>
        <li><strong>Healthcare:</strong> Improved hospitals and health programs</li>
        <li><strong>Employment:</strong> Job creation and business-friendly policies</li>
        <li><strong>Safety:</strong> Law and order maintenance</li>
      </ul>
      
      <h2>Democracy vs Dictatorship</h2>
      <table>
        <tr>
          <th>Democracy (India)</th>
          <th>Dictatorship</th>
        </tr>
        <tr>
          <td>People choose leaders</td>
          <td>Leader seizes power</td>
        </tr>
        <tr>
          <td>Free speech</td>
          <td>Censorship</td>
        </tr>
        <tr>
          <td>Regular elections</td>
          <td>No elections or fake ones</td>
        </tr>
        <tr>
          <td>Rule of law</td>
          <td>Rule by force</td>
        </tr>
      </table>
      
      <h2>Your Rights and Duties</h2>
      
      <h3>Democratic Rights:</h3>
      <ul>
        <li>Right to vote</li>
        <li>Right to contest elections</li>
        <li>Freedom of speech</li>
        <li>Right to question government</li>
      </ul>
      
      <h3>Democratic Duties:</h3>
      <ul>
        <li>Vote in every election</li>
        <li>Stay informed about issues</li>
        <li>Participate in democratic process</li>
        <li>Respect others' political views</li>
      </ul>
      
      <blockquote>
        "Democracy is not merely a form of government. It is primarily a mode of associated living." - Dr. B.R. Ambedkar
      </blockquote>
      
      <h2>Strengthen Bihar's Democracy</h2>
      <p>Every time you vote, you strengthen democracy. Every time you stay informed, you empower yourself. Bihar's future depends on active, aware citizens like you!</p>
    `,
    tags: ["Democracy", "Political Education", "Civic Duty", "Bihar"]
  }
];

// Categories for filtering
export const blogCategories = [
  "All",
  "Voter Awareness",
  "Election Process",
  "Youth Awareness",
  "Democracy Education",
  "Voting Rights"
];
